'use client';

import { Trash2 } from 'lucide-react';
import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/lib/contexts/LanguageContext';

import AddRepresentativeDialog from './AddRepresentativeDialog';

interface RepresentativesProps {
  formData: {
    ceo: string;
    boardMembers: string[];
    shareCapitalAmount: string;
    higherShareCapital: boolean;
    shareSplit: boolean;
    representativesTable: Array<{
      id: string;
      role: string;
      name: string;
      address: string;
    }>;
    [key: string]: any;
  };
  onChange: (section: string, field: string, value: any) => void;
}

function getRolePlaceholder(index: number, t: ReturnType<typeof useLanguage>['t']): string {
  if (index === 0) {
    return t.shareholder;
  }

  if (index === 1) {
    return t.boardMember;
  }

  if (index === 2) {
    return t.boardDeputy;
  }

  return '';
}

export default function RepresentativesPage({
  formData,
  onChange,
}: Readonly<RepresentativesProps>): JSX.Element {
  const { t } = useLanguage();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleTableUpdate = (index: number, field: string, value: string) => {
    const updatedTable = [...formData.representativesTable];
    updatedTable[index] = { ...updatedTable[index], [field]: value } as {
      id: string;
      role: string;
      name: string;
      address: string;
    };
    onChange('representatives', 'representativesTable', updatedTable);
  };

  const handleAddRow = () => {
    setDialogOpen(true);
  };

  const handleAddRepresentative = (data: any) => {
    const newRow = {
      id: Date.now().toString(),
      role: t.roles[data.role as keyof typeof t.roles] || data.role,
      name: `${data.firstName} ${data.lastName}`.trim(),
      address: `${data.email ? data.email + ', ' : ''}${t.countries[data.addressCountry as keyof typeof t.countries] || data.addressCountry}`,
    };
    const updatedTable = [...formData.representativesTable, newRow];
    onChange('representatives', 'representativesTable', updatedTable);
  };

  const handleRemoveRow = (index: number) => {
    if (formData.representativesTable.length > 1) {
      const updatedTable = formData.representativesTable.filter((_, i) => i !== index);
      onChange('representatives', 'representativesTable', updatedTable);
    }
  };

  return (
    <div className="space-y-8">
      {/* Share Capital Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t.shareCapitalTitle}</h3>
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">{t.shareCapitalDescription}</p>

        <RadioGroup
          value={formData.shareCapitalAmount}
          onValueChange={(value) => onChange('representatives', 'shareCapitalAmount', value)}
          className="space-y-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="25000" id="capital25k" />
            <Label htmlFor="capital25k" className="cursor-pointer">
              {t.shareCapital25k}
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <RadioGroupItem value="50000" id="capital50k" />
            <Label htmlFor="capital50k" className="cursor-pointer">
              {t.shareCapital50k}
            </Label>
          </div>
        </RadioGroup>

        <div className="mt-6 space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="higherCapital"
              checked={formData.higherShareCapital}
              onCheckedChange={(checked) =>
                onChange('representatives', 'higherShareCapital', Boolean(checked))
              }
            />
            <Label htmlFor="higherCapital" className="cursor-pointer">
              {t.orderHigherCapital}
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="shareSplit"
              checked={formData.shareSplit}
              onCheckedChange={(checked) =>
                onChange('representatives', 'shareSplit', Boolean(checked))
              }
            />
            <Label htmlFor="shareSplit" className="cursor-pointer">
              {t.orderShareSplit}
            </Label>
          </div>
        </div>
      </div>

      {/* Owners and Representatives Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t.ownersAndRepresentatives}</h3>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">{t.ownersDescription}</p>
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">{t.companyNeedsMinimum}</p>

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">{t.roleColumn}</TableHead>
                <TableHead>{t.nameColumn}</TableHead>
                <TableHead>{t.addressColumn}</TableHead>
                <TableHead className="w-[100px]">{t.removeColumn}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {formData.representativesTable.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Input
                      value={row.role}
                      onChange={(e) => handleTableUpdate(index, 'role', e.target.value)}
                      placeholder={getRolePlaceholder(index, t)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.name}
                      onChange={(e) => handleTableUpdate(index, 'name', e.target.value)}
                      placeholder=""
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.address}
                      onChange={(e) => handleTableUpdate(index, 'address', e.target.value)}
                      placeholder=""
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveRow(index)}
                      disabled={formData.representativesTable.length <= 1}
                      className="text-red-600 hover:bg-red-50 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Button
          type="button"
          variant="default"
          onClick={handleAddRow}
          className="bg-green-600 text-white hover:bg-green-700"
        >
          {t.addOwnerRepresentative}
        </Button>
      </div>

      {/* Add Representative Dialog */}
      <AddRepresentativeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onAddRepresentative={handleAddRepresentative}
        totalShares={formData.shareCapitalAmount === '25000' ? 250 : 500}
      />
    </div>
  );
}
